/*
 * KincsekKincse – saját szállításválasztó
 * ------------------------------------------------------------
 * A Parcely kiváltása. Házhozszállítás / csomagautomata választás, automatánál
 * gyors, clusterezett Leaflet (OpenStreetMap) térkép + kereshető lista.
 * A kiválasztott pont Shopify kosár-attribútumként mentődik → order "Additional details".
 *
 * Adatforrás (A fázis): a hivatalos FOXPOST automata-lista (foxplus.json), ami a
 * Packeta pontokat is tartalmazza (variant mező). Óránként frissül → ez az
 * ellenszere az elavult / nem létező pontoknak (INVALID_APM_ID). Az MPL pontok
 * külön forrásból (MPL API) jönnek majd – egyelőre nem jelennek meg.
 *
 * Teljesítmény: a térkép-JS lustán töltődik (csak automata módban); a ~7 MB-os
 * listát egyszer kérjük le, levágjuk a szükséges mezőkre, és sessionStorage-ban
 * cache-eljük (1 óra). A térkép clusterez, a lista pedig csak a látható pontokat
 * mutatja (max 200), így sosem renderelünk ezer elemet feleslegesen.
 */

const LEAFLET_JS = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.js';
const LEAFLET_CSS = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.css';
const CLUSTER_JS = 'https://cdn.jsdelivr.net/npm/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js';
const CLUSTER_CSS = 'https://cdn.jsdelivr.net/npm/leaflet.markercluster@1.5.3/dist/MarkerCluster.css';
const CLUSTER_CSS_DEFAULT = 'https://cdn.jsdelivr.net/npm/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css';

const FOXPLUS_URL = 'https://cdn.foxpost.hu/foxplus.json';
const CACHE_KEY = 'kk_points_v3';
const CACHE_TS_KEY = 'kk_points_ts_v3';
const CACHE_TTL = 60 * 60 * 1000; // 1 óra – a forrás is óránként frissül
const LIST_LIMIT = 200;
const VERSION = '3.2';

// A diagnosztikai debug eltávolítva; a DBG/dbg no-op marad, hogy a meglévő hívások ne törjenek.
const DBG = {};
function dbg() {}

// Kosár-attribútumok kulcsai (ezek látszanak az orderben, Additional details).
// A "_shipping_point" aláhúzással => vásárló felé rejtett, de adminban látszik
// és a címke-export gépileg parse-olja.
const ATTR = {
  method: 'Szállítási mód',
  carrier: 'Futárszolgálat',
  name: 'Átvételi pont',
  address: 'Átvételi pont címe',
  city: 'Átvételi pont városa',
  id: 'Csomagpont azonosító',
  json: '_shipping_point',
  // Rejtett, FIX értékű (soha nem a boltos által átnevezhető "Szállítási mód"
  // szöveg!) kulcs – a kk-shipping-point checkout-kiegészítő ebből dönti el
  // megbízhatóan, hogy a vásárló automata módot választott-e, függetlenül attól,
  // hogy a boltos hogy nevezte át a kártyák címkéit a téma-szerkesztőben.
  kind: '_szallitas_tipus',
};

const CARRIER_META = {
  foxpost: { name: 'FOXPOST', color: '#b60100' },
  packeta: { name: 'Packeta', color: '#BA1B02' },
  mpl: { name: 'MPL', color: '#4CAF58' },
};

// Ikonok + napnevek a "Kiválasztott átvételi pont" kártyához
const DETAIL_ICONS = {
  clock: '<svg viewBox="0 0 256 256" aria-hidden="true"><path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm64-88a8,8,0,0,1-8,8H128a8,8,0,0,1-8-8V72a8,8,0,0,1,16,0v48h48A8,8,0,0,1,192,128Z"/></svg>',
  mail: '<svg viewBox="0 0 256 256" aria-hidden="true"><path d="M224,48H32a8,8,0,0,0-8,8V192a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A8,8,0,0,0,224,48Zm-96,85.15L52.57,64H203.43ZM98.71,128,40,181.81V74.19Zm11.84,10.85,12,11.05a8,8,0,0,0,10.82,0l12-11.05,58,53.15H52.57ZM157.29,128,216,74.18V181.82Z"/></svg>',
  car: '<svg viewBox="0 0 256 256" aria-hidden="true"><path d="M240,104H229.2L201.42,41.5A16,16,0,0,0,186.8,32H69.2a16,16,0,0,0-14.62,9.5L26.8,104H16a8,8,0,0,0,0,16h8v80a16,16,0,0,0,16,16H64a16,16,0,0,0,16-16V184h96v16a16,16,0,0,0,16,16h24a16,16,0,0,0,16-16V120h8a8,8,0,0,0,0-16ZM69.2,48H186.8l24.89,56H44.31ZM64,200H40V184H64Zm128,0V184h24v16Zm24-32H40V120H216ZM56,144a8,8,0,0,1,8-8H80a8,8,0,0,1,0,16H64A8,8,0,0,1,56,144Zm112,0a8,8,0,0,1,8-8h16a8,8,0,0,1,0,16H176A8,8,0,0,1,168,144Z"/></svg>',
};
const CHECK_ICON = '<svg viewBox="0 0 256 256" aria-hidden="true"><path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"/></svg>';
const DAY_NAMES = { hetfo: 'Hétfő', kedd: 'Kedd', szerda: 'Szerda', csutortok: 'Csütörtök', pentek: 'Péntek', szombat: 'Szombat', vasarnap: 'Vasárnap' };
function prettyHours(v) {
  const s = String(v || '').trim();
  if (/^0?0:00\s*-\s*(24:00|23:59)$/.test(s)) return '0–24';
  return s.replace(/\s*-\s*/, '–');
}

// --- Segédfüggvények ---------------------------------------------------------

let _assetCache = {};
function loadCss(href) {
  if (_assetCache[href]) return _assetCache[href];
  _assetCache[href] = new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => reject(new Error('CSS betöltési hiba: ' + href));
    document.head.appendChild(link);
  });
  return _assetCache[href];
}

function loadScript(src) {
  if (_assetCache[src]) return _assetCache[src];
  _assetCache[src] = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('JS betöltési hiba: ' + src));
    document.head.appendChild(s);
  });
  return _assetCache[src];
}

// A markercluster a betöltéskori window.L-hez kötődik. Rögzítjük EGYSZER azt a
// Leaflet példányt, amelyiken TÉNYLEG ott a markercluster, és MINDENHOL ezt
// (LEAFLET) használjuk – így konzisztens marad akkor is, ha egy másik app
// (pl. a Parcely map_cart.js) később felülírja a globális window.L-t.
let LEAFLET = null;
let _leafletPromise = null;
function ensureLeaflet(bundleUrl) {
  if (_leafletPromise) return _leafletPromise;
  _leafletPromise = (async () => {
    await loadCss(LEAFLET_CSS);
    const isL = (x) => x && typeof x.map === 'function';
    // Saját, IZOLÁLT leaflet bundle (window.KKL) – nem függ a globális window.L-től,
    // így másik app (pl. Parcely) Leafletje nem zavar. Saját rács-clusterezést használunk.
    if (bundleUrl) {
      try { await loadScript(bundleUrl); } catch (e) { /* fallback lent */ }
    }
    if (isL(window.KKL)) { LEAFLET = window.KKL; return LEAFLET; }
    // Fallback: CDN leaflet (a captured referencia closure-safe)
    await loadScript(LEAFLET_JS);
    LEAFLET = isL(window.KKL) ? window.KKL : window.L;
    return LEAFLET;
  })();
  return _leafletPromise;
}

// Ékezet-érzéketlen, kisbetűs normalizálás kereséshez
function norm(s) {
  return (s || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

function esc(s) {
  return (s || '').toString().replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// Cím szöveg egy pontból
function addrOf(p) {
  return `${p.zip || ''} ${p.city || ''}${p.street ? ', ' + p.street : ''}`.trim();
}

// Rendezéshez elég a négyzetes "távolság" a lat/lng-ben
function dist2(aLat, aLng, bLat, bLng) {
  const dx = aLat - bLat;
  const dy = aLng - bLng;
  return dx * dx + dy * dy;
}

// Valós távolság km-ben (egyszerű, Magyarországra bőven pontos közelítés)
function distKm(aLat, aLng, bLat, bLng) {
  const dLat = (bLat - aLat) * 111.0;
  const dLng = (bLng - aLng) * 111.0 * Math.cos((aLat + bLat) / 2 * Math.PI / 180);
  return Math.sqrt(dLat * dLat + dLng * dLng);
}

// Távolság szöveg: 1 km alatt méter (10 m pontosság), felette "1,2 km"
function fmtDist(km) {
  if (!isFinite(km)) return '';
  if (km < 1) return (Math.round(km * 1000 / 10) * 10) + ' m';
  return km.toFixed(1).replace('.', ',') + ' km';
}

// A kosár összegzője a kiválasztott szállítási módból (kosár-attribútumok) számolja a becsült szállítási díjat
// (snippets/cart-summary.liquid). Attribútum-írás után a téma saját útvonalán újrarajzoltatjuk a kosarat; ez a
// választó (data-skip-subtree-update) a frissítésnél védett, tehát nem rajzolódik újra.
let _summaryTimer = 0;
function refreshCartSummary(sourceEl) {
  clearTimeout(_summaryTimer);
  _summaryTimer = setTimeout(async () => {
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      const { CartUpdateEvent } = await import('@theme/events');
      (sourceEl || document).dispatchEvent(
        new CartUpdateEvent(cart, 'kk-shipping-selector', { source: 'kk-shipping-selector', itemCount: cart.item_count })
      );
    } catch (e) {
      /* a következő kosár-frissítés úgyis igazít */
    }
  }, 150);
}

async function updateCartAttributes(attrs) {
  const url = (window.Theme && window.Theme.routes && window.Theme.routes.cart_update_url) || '/cart/update';
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: attrs }),
  });
  if (!res.ok) throw new Error('Kosár frissítési hiba: ' + res.status);
  return res.json();
}

// findme HTML -> tiszta megközelítés-szöveg (az első bekezdés, a "Fizetési…" rész előtt)
function cleanFindme(html) {
  if (!html) return '';
  let s = String(html).split(/<br\s*\/?>\s*<br\s*\/?>/i)[0];
  s = s.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
  // ha a "leírás" valójában csak fizetés/szolgáltatás infó (nincs valódi megközelítés) -> üres
  if (/^(fizet|el[ée]rhet[őo] szolg[áa]ltat)/i.test(s)) return '';
  return s;
}

// nyitvatartás normalizálás. Visszaad: ha mind a 7 nap azonos -> egyetlen STRING
// (tömör, "minden nap"); ha eltérnek -> {nap: érték} OBJEKTUM; ha nincs -> null.
function normalizeOpen(open) {
  if (!open || typeof open !== 'object') return null;
  const days = ['hetfo', 'kedd', 'szerda', 'csutortok', 'pentek', 'szombat', 'vasarnap'];
  const out = {};
  let any = false;
  for (const d of days) {
    const v = (open[d] == null ? '' : String(open[d])).trim();
    if (v) { out[d] = v; any = true; }
  }
  if (!any) return null;
  const vals = days.map((d) => out[d]);
  if (vals.every((v) => v && v === vals[0])) return vals[0]; // mind a 7 nap azonos
  return out;
}

// foxplus.json rekord => belső pont-objektum (mezők + nyitvatartás + megközelítés)
function mapFoxplus(r) {
  if (r.geolat == null || r.geolng == null || r.place_id == null) return null;
  const variant = r.variant || '';
  return {
    id: r.place_id, // egyedi fizikai automata-azonosító (kiválasztáshoz)
    operatorId: r.operator_id, // ezt küldjük a FOXPOST címkének (destination)
    carrier: /packeta/i.test(variant) ? 'packeta' : 'foxpost',
    variant,
    name: r.name || '',
    zip: r.zip || '',
    city: r.city || '',
    street: r.street || '',
    lat: r.geolat,
    lng: r.geolng,
    open: normalizeOpen(r.open),
    directions: cleanFindme(r.findme),
  };
}

// --- A komponens -------------------------------------------------------------

class ShippingSelector extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    DBG.init++; dbg('connectedCallback #' + DBG.init);

    this.config = this._readConfig();
    this.points = [];
    this._pointsLoaded = false;
    this.state = {
      method: null,
      carriers: new Set(Object.keys(this.config.carriers).filter((k) => this.config.carriers[k])),
      allMode: true,   // "Összes" – külön állapot, nem kombinálható a futárcég-szűrőkkel
      query: '',
      geo: null,
      userLoc: null,   // "Helyem használata" pozíció (távolság kijelzés + rendezés)
      selected: null,
      detailsMode: false,   // kiválasztott-pont részletek nézet (vs. lista böngészés)
    };
    this.map = null;
    this.layer = null;
    this.markersById = {};

    if (this.config.accent) this.style.setProperty('--kk-accent', this.config.accent);

    this._cacheDom();
    this._bindMethodInputs();
    this._bindPriceInfo();
    this._restoreFromCart();
  }

  _readConfig() {
    const el = this.querySelector('.kk-ship-config');
    let cfg = {};
    try { cfg = JSON.parse(el.textContent); } catch (e) { /* defaults */ }
    return {
      initialMethod: cfg.initialMethod || '',
      initialPoint: (() => { try { return cfg.initialPointJson ? JSON.parse(cfg.initialPointJson) : null; } catch (e) { return null; } })(),
      prices: cfg.prices || { home: 1490, parcel: 990 },
      labels: cfg.labels || { home: 'Házhozszállítás', parcel: 'Csomagautomata / csomagpont' },
      carriers: cfg.carriers || { foxpost: true, packeta: true, mpl: true },
      accent: cfg.accent || '',
      pointsUrl: cfg.pointsUrl || '',
      mplUrl: cfg.mplUrl || '',
      bundleUrl: cfg.bundleUrl || '',
      logos: cfg.logos || null,
      listLogos: cfg.listLogos || null,
    };
  }

  _cacheDom() {
    this.panel = this.querySelector('[data-panel]');
    this.errorEl = this.querySelector('[data-error]');
    this.promptEl = this.querySelector('[data-prompt]');
    this.step2El = this.querySelector('[data-step2]');
    this.bannerEl = this.querySelector('[data-banner]');
    this.homeEl = this.querySelector('[data-home]');
    this.methodInputs = Array.from(this.querySelectorAll('input[name="kk-ship-method"]'));
  }

  _bindMethodInputs() {
    this.methodInputs.forEach((input) => {
      input.addEventListener('change', () => { if (input.checked) this.setMethod(input.value); });
    });
  }

  // A "990 Ft-tól" melletti ⓘ – a díj-bontó popover nyit/zár (a pontra kattintás ne váltson módot).
  _bindPriceInfo() {
    const btn = this.querySelector('[data-price-info]');
    const pop = this.querySelector('[data-price-pop]');
    if (!btn || !pop) return;
    const close = () => { pop.hidden = true; btn.setAttribute('aria-expanded', 'false'); };
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const willOpen = pop.hidden;
      pop.hidden = !willOpen;
      btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    });
    document.addEventListener('click', (e) => {
      if (!pop.hidden && !pop.contains(e.target) && !btn.contains(e.target)) close();
    });
    this.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  }

  _restoreFromCart() {
    const m = this.config.initialMethod;
    if (m === 'parcel' || this.config.labels.parcel === m) {
      if (this.config.initialPoint) this.state.selected = this.config.initialPoint;
      this._checkInput('parcel');
      this.setMethod('parcel', true);
    } else if (m === 'home' || this.config.labels.home === m) {
      this._checkInput('home');
      this.setMethod('home', true);
    } else {
      this._applyCheckoutGate();
    }
  }

  _checkInput(value) {
    const input = this.methodInputs.find((i) => i.value === value);
    if (input) input.checked = true;
  }

  setMethod(method, restoring = false) {
    this.state.method = method;
    this._clearError();

    if (method === 'home') {
      this.panel.hidden = true;
      this.panel.innerHTML = '';
      this._setStep2('2. Szállítási információk');
      if (this.bannerEl) this.bannerEl.hidden = true;   // parcel banner rejtve
      if (this.homeEl) this.homeEl.hidden = false;      // házhoz-infó (kártya + banner) látszik
      if (!restoring) this._writeHome();
    } else if (method === 'parcel') {
      this.panel.hidden = false;
      this._setStep2('2. Átvételi pont kiválasztása');
      if (this.bannerEl) this.bannerEl.hidden = false;  // automata módban látszik
      if (this.homeEl) this.homeEl.hidden = true;       // házhoz-infó rejtve
      this._renderParcelPanel();
      if (!restoring) this._writeMethodOnly();
    }
    this._applyCheckoutGate();
  }

  // A 2. lépés fejléce a választott mód szerint vált (és csak mód-választás után látszik)
  _setStep2(text) {
    if (!this.step2El) return;
    this.step2El.textContent = text;
    this.step2El.hidden = false;
  }

  // ---- Automata panel ----
  _renderParcelPanel() {
    this.panel.innerHTML = `
      <div class="kk-ship__filters" data-filters>
        <div class="kk-ship__chips" data-chips></div>
        <div class="kk-ship__search">
          <div class="kk-ship__search-field">
            <input type="search" class="kk-ship__search-input" data-search placeholder="Keresés városra, irányítószámra, névre…" autocomplete="off">
            <svg class="kk-ship__search-icon" viewBox="0 0 256 256" aria-hidden="true"><path d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z"></path></svg>
          </div>
          <button type="button" class="kk-ship__loc-btn" data-locate>
            <svg class="kk-ship__loc-icon" viewBox="0 0 256 256" aria-hidden="true"><path d="M128,64a40,40,0,1,0,40,40A40,40,0,0,0,128,64Zm0,64a24,24,0,1,1,24-24A24,24,0,0,1,128,128Zm0-112a88.1,88.1,0,0,0-88,88c0,31.4,14.51,64.68,42,96.25a254.19,254.19,0,0,0,41.45,38.3,8,8,0,0,0,9.18,0A254.19,254.19,0,0,0,174,200.25c27.45-31.57,42-64.85,42-96.25A88.1,88.1,0,0,0,128,16Zm0,206c-16.53-13-72-60.75-72-118a72,72,0,0,1,144,0C200,161.23,144.53,209,128,222Z"></path></svg>
            <span>Helyem használata</span>
          </button>
        </div>
      </div>
      <div class="kk-ship__count" data-count></div>
      <div class="kk-ship__layout">
        <div class="kk-ship__map" data-map><div class="kk-ship__map-loading">Térkép betöltése…</div></div>
        <ul class="kk-ship__list" data-list></ul>
        <div class="kk-ship__details" data-details hidden></div>
      </div>
    `;

    this.chipsEl = this.panel.querySelector('[data-chips]');
    this.countEl = this.panel.querySelector('[data-count]');
    this.mapEl = this.panel.querySelector('[data-map]');
    this.listEl = this.panel.querySelector('[data-list]');
    this.detailsEl = this.panel.querySelector('[data-details]');

    const searchInput = this.panel.querySelector('[data-search]');
    this._searchInput = searchInput;
    const runSearch = () => { this.state.query = searchInput.value.trim(); this._applySearch(); };
    let searchDeb;
    searchInput.addEventListener('input', () => { clearTimeout(searchDeb); searchDeb = setTimeout(runSearch, 350); });
    searchInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); clearTimeout(searchDeb); runSearch(); } });
    const locBtn = this.panel.querySelector('[data-locate]');
    if (locBtn) locBtn.addEventListener('click', () => this._useMyLocation(locBtn));

    this.state.detailsMode = !!this.state.selected;
    this._renderDetails();
    this._applyPanelMode();
    this._renderList(); // "Pontok betöltése…" amíg nincs adat
    this._initMap();

    this._ensurePoints()
      .then(() => {
        DBG.points = this.points.length; dbg('points loaded: ' + this.points.length);
        // visszaállított pont frissítése a teljes adatra (nyitvatartás/megközelítés)
        if (this.state.selected) {
          const full = this.points.find((x) => String(x.id) === String(this.state.selected.id));
          if (full) { this.state.selected = full; if (this.state.detailsMode) this._renderDetails(); }
        }
        this._renderChips(); this._refreshPoints();
      })
      .catch((err) => {
        DBG.lastError = 'points load: ' + (err && err.message ? err.message : err); dbg('points load FAILED');
        console.warn('[shipping-selector] pont-betöltés hiba:', err);
        if (this.listEl) this.listEl.innerHTML = '<li class="kk-ship__empty">A csomagpontok betöltése nem sikerült. Frissítsd az oldalt, vagy próbáld kicsit később.</li>';
      });
  }

  // A FOXPOST(+Packeta) pontok betöltése (cache → fetch → slim → cache)
  async _ensurePoints() {
    if (this._pointsLoaded) return;
    // 1) gyors cache (1 óra)
    try {
      const ts = +sessionStorage.getItem(CACHE_TS_KEY) || 0;
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (raw && Date.now() - ts < CACHE_TTL) {
        this.points = JSON.parse(raw);
        this._pointsLoaded = true;
        return;
      }
    } catch (e) { /* cache nélkül is megy */ }

    // 2) elsődlegesen a kicsi téma-asset (azonos eredet, gyors, tömörített),
    //    ha nincs / hibás, fallback az élő FOXPOST CDN-re (nagy, 7 MB)
    // a téma-assetek párhuzamosan: FOXPOST+Packeta és MPL, majd összefűzve
    const jobs = [];
    if (this.config.pointsUrl) jobs.push(this._fetchPoints(this.config.pointsUrl, false).catch((e) => { console.warn('[shipping-selector] FOXPOST feed hiba:', e); return null; }));
    if (this.config.mplUrl) jobs.push(this._fetchPoints(this.config.mplUrl, false).catch((e) => { console.warn('[shipping-selector] MPL feed hiba:', e); return null; }));
    let pts = [];
    for (const r of await Promise.all(jobs)) { if (Array.isArray(r)) pts = pts.concat(r); }
    // ha egyik asset sem jött, fallback az élő FOXPOST CDN-re
    if (!pts.length) pts = await this._fetchPoints(FOXPLUS_URL, true);

    this.points = pts;
    this._pointsLoaded = true;
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(pts));
      sessionStorage.setItem(CACHE_TS_KEY, String(Date.now()));
    } catch (e) { /* quota – nem baj */ }
  }

  // Pontok betöltése egy URL-ről, időkorláttal. live=true => nyers foxplus formátum.
  async _fetchPoints(url, live) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 20000);
    try {
      const res = await fetch(url, { headers: { Accept: 'application/json' }, signal: ctrl.signal });
      if (!res.ok) throw new Error(url + ' → ' + res.status);
      const data = await res.json();
      if (!live) return data; // a téma-asset már a kész formátum
      const seen = new Set();
      const out = [];
      for (const r of data) {
        const p = mapFoxplus(r);
        if (!p || seen.has(p.id)) continue;
        seen.add(p.id);
        out.push(p);
      }
      return out;
    } finally {
      clearTimeout(timer);
    }
  }

  // Chipek: "Összes" (külön állapot) + az engedélyezett ÉS adattal rendelkező futárcégek.
  // "Összes" aktív = minden látszik, a futárcég-chipek inaktívak. Egy futárcégre kattintva
  // az "Összes" kikapcsol; a futárcégek egymással KOMBINÁLHATÓK (több is aktív lehet).
  _renderChips() {
    if (!this.chipsEl) return;
    const present = new Set(this.points.map((p) => p.carrier));
    const enabled = Object.keys(this.config.carriers).filter((c) => this.config.carriers[c]);
    const avail = enabled.filter((c) => present.has(c));

    // a kiválasztott halmazt az elérhetőkre szűkítjük; ha üresre fogyna (nem "Összes"), visszaáll "Összes"-re
    this.state.carriers = new Set([...this.state.carriers].filter((c) => avail.includes(c)));
    if (!this.state.allMode && this.state.carriers.size === 0) this.state.allMode = true;

    const allActive = this.state.allMode;
    let html = `<button type="button" class="kk-ship__chip kk-ship__chip--all${allActive ? ' is-active' : ''}" data-all>Összes</button>`;
    html += avail
      .map((c) => {
        const active = !allActive && this.state.carriers.has(c);
        return `<button type="button" class="kk-ship__chip${active ? ' is-active' : ''}" data-carrier="${c}" style="--chip:${CARRIER_META[c].color}"><span class="kk-ship__chip-dot"></span>${CARRIER_META[c].name}</button>`;
      })
      .join('');
    this.chipsEl.innerHTML = html;

    const allBtn = this.chipsEl.querySelector('[data-all]');
    if (allBtn) allBtn.addEventListener('click', () => this._selectAll());
    this.chipsEl.querySelectorAll('[data-carrier]').forEach((btn) => {
      btn.addEventListener('click', () => this._toggleCarrier(btn.dataset.carrier));
    });
  }

  // "Összes" – külön állapot: minden futárcég látszik, a futárcég-chipek kikapcsolnak
  _selectAll() {
    this.state.allMode = true;
    this._renderChips();
    this._refreshPoints();
  }

  _toggleCarrier(carrier) {
    if (this.state.allMode) {
      this.state.allMode = false;           // "Összes"-ből egy konkrét futárcégre váltunk
      this.state.carriers = new Set([carrier]);
    } else if (this.state.carriers.has(carrier)) {
      this.state.carriers.delete(carrier);
      if (this.state.carriers.size === 0) this.state.allMode = true; // semmi sem maradt -> "Összes"
    } else {
      this.state.carriers.add(carrier);     // kombinálható: több futárcég együtt
    }
    this._renderChips();
    this._refreshPoints();
  }

  // A térképre kerülő pontok: "Összes"-nél minden engedélyezett, egyébként a kiválasztott futárcégek
  _markerPoints() {
    if (this.state.allMode) return this.points.filter((p) => this.config.carriers[p.carrier]);
    return this.points.filter((p) => this.state.carriers.has(p.carrier));
  }

  // Keresési találatok (szöveg + futárszűrés)
  _searchMatches() {
    const q = norm(this.state.query);
    const base = this._markerPoints();
    if (!q) return base;
    // Irányítószám: PREFIX-egyezés (NEM szám-közelség!). A leghosszabb prefix,
    // amire van pont: "4000" → "400" → "40". A 40xx mind Debrecen környéke, így
    // földrajzilag helyes marad (a szám-közelség 3999 Hollóházát adná).
    if (/^\d{2,4}$/.test(q)) {
      for (let len = q.length; len >= 2; len--) {
        const pref = q.slice(0, len);
        const hits = base.filter((p) => norm(p.zip).startsWith(pref));
        if (hits.length) return hits;
      }
      return [];
    }
    // Település: előbb PONTOS név, majd amivel KEZDŐDIK (így az "eger" nem hozza a
    // "zalaegerszeg"-et), végül bárhol (város / pont-név / utca) tartalmazza.
    const exactCity = base.filter((p) => norm(p.city) === q);
    if (exactCity.length) return exactCity;
    const startCity = base.filter((p) => norm(p.city).startsWith(q));
    if (startCity.length) return startCity;
    return base.filter(
      (p) => norm(p.city).includes(q) || norm(p.name).includes(q) || norm(p.street).includes(q)
    );
  }

  // A listában mutatott pontok: keresésnél a találatok, egyébként a látható
  // térkép-területen lévők (a középhez közeliek elöl), maximum LIST_LIMIT.
  _listPoints() {
    if (this.state.query) {
      const text = this._searchMatches();
      if (text.length) return text.slice(0, LIST_LIMIT);
      // geokódolt középponthoz legközelebbi pontok (pl. irányítószámos keresés)
      if (this.state.geo) {
        const g = this.state.geo;
        return this._markerPoints()
          .slice()
          .sort((a, z) => distKm(g.lat, g.lng, a.lat, a.lng) - distKm(g.lat, g.lng, z.lat, z.lng))
          .slice(0, 50);
      }
      return [];
    }
    const base = this._markerPoints();
    if (this.map) {
      const b = this.map.getBounds();
      const o = this.state.userLoc || this.map.getCenter();   // "helyem" ha van, egyébként a térkép közepe
      return base
        .filter((p) => b.contains([p.lat, p.lng]))
        .sort((a, z) => distKm(o.lat, o.lng, a.lat, a.lng) - distKm(o.lat, o.lng, z.lat, z.lng))
        .slice(0, LIST_LIMIT);
    }
    return base.slice(0, LIST_LIMIT);
  }

  // ---- Térkép ----
  async _initMap() {
    try {
      const L = await ensureLeaflet(this.config.bundleUrl);
      if (!this.mapEl || !this.mapEl.isConnected) return;
      // FONTOS: csak akkor hozzuk létre a térképet, ha a konténernek MÁR van mérete.
      await this._waitForSize(this.mapEl);
      if (!this.mapEl || !this.mapEl.isConnected) return;
      dbg('map size before init: ' + this.mapEl.offsetWidth + 'x' + this.mapEl.offsetHeight);
      this.mapEl.innerHTML = '';
      this.map = L.map(this.mapEl, { scrollWheelZoom: true }).setView([47.1625, 19.5033], 7);
      DBG.mapReady = true; DBG.mapSize = this.mapEl.offsetWidth + 'x' + this.mapEl.offsetHeight; dbg('map created (' + DBG.mapSize + ')');
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '&copy; OpenStreetMap' }).addTo(this.map);
      this.layer = L.layerGroup().addTo(this.map);
      this.spiderLayer = L.layerGroup().addTo(this.map);   // szétlegyezett (spiderfy) jelölők
      // Mobil: 1 ujj = oldalgörgetés, 2 ujj = térkép mozgatás (ne "ragadjon be" a görgetés)
      if (('ontouchstart' in window) || navigator.maxTouchPoints > 0) {
        if (this.map.dragging) this.map.dragging.disable();
        this.mapEl.addEventListener('touchstart', (e) => {
          if (this.map.dragging && e.touches && e.touches.length >= 2) this.map.dragging.enable();
        }, { passive: true });
        this.mapEl.addEventListener('touchend', () => { if (this.map.dragging) this.map.dragging.disable(); }, { passive: true });
        const hint = document.createElement('div');
        hint.className = 'kk-ship__map-hint';
        hint.textContent = 'Két ujjal mozgasd a térképet';
        this.mapEl.appendChild(hint);
      }
      this.map.on('moveend', () => { this._renderMap(); if (!this.state.query) this._renderList(); });
      if (this._pointsLoaded) this._refreshPoints();
      // biztonsági utó-méretezés + folyamatos figyelés (későbbi layout-változásra)
      requestAnimationFrame(() => this.map && this.map.invalidateSize(false));
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(() => { if (this.map) this.map.invalidateSize(false); });
        this._ro.observe(this.mapEl);
      }
    } catch (err) {
      if (this.mapEl) this.mapEl.innerHTML = '<div class="kk-ship__map-loading">A térkép most nem elérhető – válassz a lenti listából.</div>';
      console.warn('[shipping-selector] Leaflet betöltés sikertelen:', err);
    }
  }

  // Megvárja (max 3s), amíg a konténer tényleg méretet kap, hogy a térkép
  // helyes vetítéssel induljon (ez szünteti meg a "clusterek egy pontban" bugot).
  _waitForSize(el) {
    return new Promise((resolve) => {
      if (el && el.offsetWidth > 0 && el.offsetHeight > 0) { resolve(); return; }
      let done = false;
      let ro = null;
      const finish = () => {
        if (done) return;
        done = true;
        if (ro) ro.disconnect();
        clearInterval(iv);
        clearTimeout(to);
        resolve();
      };
      const check = () => { if (el && el.offsetWidth > 0 && el.offsetHeight > 0) finish(); };
      if ('ResizeObserver' in window) { ro = new ResizeObserver(check); ro.observe(el); }
      const iv = setInterval(check, 100);
      const to = setTimeout(finish, 3000);
    });
  }

  _iconFor(p) {
    const L = LEAFLET;
    const meta = CARRIER_META[p.carrier] || { color: '#888' };
    const selected = this.state.selected && this.state.selected.id === p.id;
    // Packeta: a lista-logót használjuk (teli lap), ami kitölti a kört (nincs fehér csík)
    const fill = p.carrier === 'packeta';
    const logo = (fill && this.config.listLogos && this.config.listLogos[p.carrier])
      || (this.config.logos && this.config.logos[p.carrier]) || '';
    return L.divIcon({
      className: 'kk-ship__pin' + (selected ? ' kk-ship__pin--selected' : ''),
      html: '<span class="kk-ship__pin-wrap" style="--pin:' + meta.color + '">'
        + '<span class="kk-ship__pin-marker' + (fill ? ' kk-ship__pin-marker--fill' : '') + '">'
        + (logo ? '<img class="kk-ship__pin-logo" src="' + esc(logo) + '" alt="" onerror="this.style.display=\'none\'">' : '')
        + '</span>'
        + (selected ? '<span class="kk-ship__pin-check">' + CHECK_ICON + '</span>' : '')
        + '</span>',
      iconSize: [30, 40],
      iconAnchor: [15, 40],
      popupAnchor: [0, -42],
    });
  }

  // Saját rács-clusterezés (markercluster helyett, ami a globális window.L-t használná).
  _renderMap() {
    if (!this.map || !this.layer || !LEAFLET) return;
    const L = LEAFLET;
    this.layer.clearLayers();
    if (this.spiderLayer) this.spiderLayer.clearLayers();   // szétlegyezés összecsukása
    this.markersById = {};
    const zoom = this.map.getZoom();
    const bounds = this.map.getBounds().pad(0.3);
    const visible = this._markerPoints().filter((p) => bounds.contains([p.lat, p.lng]));
    // pixel-rács szerinti csoportosítás (~64px cellák az aktuális zoomon)
    const CELL = 64;
    const buckets = {};
    for (const p of visible) {
      const pt = this.map.project([p.lat, p.lng], zoom);
      const key = Math.floor(pt.x / CELL) + ':' + Math.floor(pt.y / CELL);
      (buckets[key] || (buckets[key] = [])).push(p);
    }
    let single = 0;
    for (const key in buckets) {
      const grp = buckets[key];
      if (grp.length === 1) {
        const p = grp[0];
        const sel = this.state.selected && this.state.selected.id === p.id;
        const m = L.marker([p.lat, p.lng], { icon: this._iconFor(p), zIndexOffset: sel ? 100000 : 0 });
        m.on('click', () => this.selectPoint(p));
        m.bindPopup(() => this._popupHtml(p));
        this.markersById[p.id] = m;
        this.layer.addLayer(m);
        single++;
      } else {
        let lat = 0, lng = 0;
        grp.forEach((p) => { lat += p.lat; lng += p.lng; });
        lat /= grp.length; lng /= grp.length;
        const size = grp.length < 10 ? 36 : grp.length < 100 ? 44 : 52;
        const cm = L.marker([lat, lng], {
          icon: L.divIcon({
            className: 'kk-ship__cluster',
            html: '<div class="kk-ship__cluster-inner">' + grp.length + '</div>',
            iconSize: [size, size],
            iconAnchor: [size / 2, size / 2],
          }),
        });
        cm.on('click', () => {
          const maxZ = Math.min(this.map.getMaxZoom() || 19, 19);
          // ha már a max. nagyításon vagyunk, VAGY a pontok ott is összeérnének,
          // körben szétlegyezzük (spiderfy) – különben tovább nagyítunk rájuk.
          if (zoom >= maxZ || this._staysTogether(grp, maxZ)) {
            this.layer.removeLayer(cm);   // a buborék helyett a szétlegyezett jelölők
            this._spiderfy(grp);
          } else {
            this.map.setView([lat, lng], Math.min(zoom + 3, maxZ));
          }
        });
        this.layer.addLayer(cm);
      }
    }
    DBG.markers = visible.length; dbg('map: ' + visible.length + ' pont a nézetben, ' + single + ' egyedi (zoom ' + zoom + ')');
  }

  // Igaz, ha a csoport pontjai a megadott zoomon is ~egy helyre esnének (a jelölők
  // takarnák egymást) – ilyenkor a nagyítás nem segít, szét kell legyezni.
  _staysTogether(grp, z) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of grp) {
      const pt = this.map.project([p.lat, p.lng], z);
      if (pt.x < minX) minX = pt.x;
      if (pt.x > maxX) maxX = pt.x;
      if (pt.y < minY) minY = pt.y;
      if (pt.y > maxY) maxY = pt.y;
    }
    return (maxX - minX) < 40 && (maxY - minY) < 40;   // < ~egy jelölő szélesség
  }

  // Egymásra eső pontok körben való "szétlegyezése" (spiderfy), hogy külön
  // kattinthatók legyenek. A jelölők látszólagos (pixel-eltolt) helyre kerülnek,
  // de a kattintás a VALÓS pontot választja ki. A rétegét a következő _renderMap törli.
  _spiderfy(grp) {
    const L = LEAFLET;
    if (!L || !this.spiderLayer) return;
    this.spiderLayer.clearLayers();
    const z = this.map.getZoom();
    let cx = 0, cy = 0;
    grp.forEach((p) => { const pt = this.map.project([p.lat, p.lng], z); cx += pt.x; cy += pt.y; });
    cx /= grp.length; cy /= grp.length;
    const centerLL = this.map.unproject(L.point(cx, cy), z);
    const n = grp.length;
    const R = Math.max(34, 11 * n);   // kör sugara px-ben (több pont → nagyobb kör)
    grp.forEach((p, i) => {
      const a = (2 * Math.PI * i) / n - Math.PI / 2;   // felülről indul, óramutató szerint
      const ll = this.map.unproject(L.point(cx + R * Math.cos(a), cy + R * Math.sin(a)), z);
      this.spiderLayer.addLayer(L.polyline([centerLL, ll],
        { color: '#888888', weight: 1.5, opacity: 0.7, interactive: false }));
      const sel = this.state.selected && this.state.selected.id === p.id;
      const m = L.marker(ll, { icon: this._iconFor(p), zIndexOffset: (sel ? 100000 : 5000) + i });
      m.on('click', () => this.selectPoint(p));
      m.bindPopup(() => this._popupHtml(p));
      this.spiderLayer.addLayer(m);
    });
  }

  _updateMarkerSelection(prevId, newId) {
    [prevId, newId].forEach((id) => {
      if (id != null && this.markersById[id]) {
        const p = this.points.find((x) => x.id === id);
        if (p) this.markersById[id].setIcon(this._iconFor(p));
      }
    });
  }

  _popupHtml(p) {
    const meta = CARRIER_META[p.carrier] || { name: '', color: '#888' };
    return `<div class="kk-ship__popup">
      <strong>${esc(p.name)}</strong>
      <span class="kk-ship__popup-carrier" style="color:${meta.color}">${meta.name}</span>
      <span>${esc(addrOf(p))}</span>
    </div>`;
  }

  async _applySearch() {
    const q = this.state.query;
    if (!q) { this.state.geo = null; this._renderList(); return; }

    // 1) szöveges találatok (város / irányítószám / név / utca)
    const text = this._searchMatches();
    dbg('search "' + q + '" -> szoveg talalat: ' + text.length);
    if (text.length) {
      this.state.geo = null;
      if (this.map && LEAFLET) {
        const bounds = LEAFLET.latLngBounds(text.map((p) => [p.lat, p.lng]));
        if (bounds.isValid()) this.map.fitBounds(bounds.pad(0.2), { maxZoom: 15 });
      }
      this._renderList();
      return;
    }

    // 2) nincs helyi találat → ingyenes geokódolás (ritka ir.szám / kis település / utca)
    if (this.countEl) this.countEl.textContent = 'Keresés…';
    const center = await this._geocode(q);
    if (q !== this.state.query) return; // időközben tovább gépelt
    this.state.geo = center;
    if (center && this.map) this.map.setView([center.lat, center.lng], 12);
    this._renderList();
  }

  // "Helyem használata" – böngésző geolokáció → a térkép a felhasználó helyére ugrik,
  // a moveend pedig a hozzá legközelebbi pontokat hozza elő. Hiba/elutasítás esetén üzenet.
  _useMyLocation(btn) {
    if (!('geolocation' in navigator)) {
      this._showError('A böngésződ nem támogatja a helymeghatározást. Keress városra vagy irányítószámra.');
      return;
    }
    const label = btn ? btn.querySelector('span') : null;
    const prev = label ? label.textContent : '';
    if (label) label.textContent = 'Keresés…';
    if (btn) btn.disabled = true;
    const restore = () => { if (label) label.textContent = prev; if (btn) btn.disabled = false; };
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        restore();
        this._clearError();
        if (this._searchInput) this._searchInput.value = '';
        this.state.query = '';
        this.state.geo = null;
        this.state.userLoc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        if (this.map) this.map.setView([this.state.userLoc.lat, this.state.userLoc.lng], 12);
        else this._renderList();
      },
      (err) => {
        restore();
        this._showError(err && err.code === 1
          ? 'A helymeghatározás nincs engedélyezve. Engedélyezd a böngészőben, vagy keress városra/irányítószámra.'
          : 'Nem sikerült meghatározni a helyzeted. Próbáld újra, vagy keress városra/irányítószámra.');
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  }

  // Ingyenes geokódolás (Photon), Magyarországra hangolva. Hiba esetén null.
  async _geocode(q) {
    try {
      const url = 'https://photon.komoot.io/api/?limit=1&lang=default&lat=47.16&lon=19.5&q=' + encodeURIComponent(q + ', Magyarország');
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 8000);
      const res = await fetch(url, { signal: ctrl.signal });
      clearTimeout(t);
      if (!res.ok) return null;
      const data = await res.json();
      const f = data && data.features && data.features[0];
      if (!f || !f.geometry || !f.geometry.coordinates) return null;
      const [lon, lat] = f.geometry.coordinates;
      if (lat < 45 || lat > 49 || lon < 16 || lon > 23) return null; // HU-n kívül: eldobjuk
      return { lat, lng: lon };
    } catch (e) { return null; }
  }

  _refreshPoints() {
    this._renderMap();
    this._renderList();
  }

  // ---- Lista ----
  // A lista-elem futárcég-logója (a régi színes pötty helyett); MPL zöld háttérrel.
  _listLogo(p) {
    const src = this.config.listLogos && this.config.listLogos[p.carrier];
    const meta = CARRIER_META[p.carrier] || { name: '', color: '#888' };
    if (!src) return `<span class="kk-ship__item-dot" style="background:${meta.color}"></span>`;
    return `<span class="kk-ship__item-logo kk-ship__item-logo--${p.carrier}"><img src="${esc(src)}" alt="${esc(meta.name)}" loading="lazy"></span>`;
  }

  _renderList() {
    if (!this.listEl) return;
    if (!this._pointsLoaded) {
      this.listEl.innerHTML = '<li class="kk-ship__empty">Pontok betöltése…</li>';
      if (this.countEl) this.countEl.textContent = '';
      return;
    }
    const pts = this._listPoints();
    if (this.countEl) {
      if (this.state.query) {
        const textCount = this._searchMatches().length;
        this.countEl.textContent = textCount ? `${textCount} találat` : (this.state.geo ? 'Legközelebbi pontok' : '');
      } else {
        const total = this._markerPoints().length;
        this.countEl.textContent = `${total} átvételi pont – mozgasd a térképet vagy keress`;
      }
    }
    if (!pts.length) {
      this.listEl.innerHTML = `<li class="kk-ship__empty">${this.state.query ? 'Nincs találat erre a keresésre.' : 'Nagyíts rá a térképre, vagy keress egy településre.'}</li>`;
      return;
    }
    const userLoc = this.state.userLoc;
    this.listEl.innerHTML = pts
      .map((p) => {
        const selected = this.state.selected && this.state.selected.id === p.id;
        const dist = userLoc ? fmtDist(distKm(userLoc.lat, userLoc.lng, p.lat, p.lng)) : '';
        return `<li class="kk-ship__item${selected ? ' is-selected' : ''}" data-id="${esc(p.id)}">
          ${this._listLogo(p)}
          <span class="kk-ship__item-body">
            <span class="kk-ship__item-name">${esc(p.name)}</span>
            <span class="kk-ship__item-addr">${esc(addrOf(p))}</span>
          </span>
          <span class="kk-ship__item-right">
            ${dist ? `<span class="kk-ship__item-dist">${dist}</span>` : ''}
            <span class="kk-ship__item-action">${selected ? 'Kiválasztva' : 'Választ'}</span>
          </span>
        </li>`;
      })
      .join('');
    this.listEl.querySelectorAll('.kk-ship__item').forEach((li) => {
      li.addEventListener('click', () => {
        const p = this.points.find((x) => String(x.id) === li.dataset.id);
        if (!p) return;
        this.selectPoint(p);
        if (this.map) this.map.setView([p.lat, p.lng], Math.max(this.map.getZoom(), 15));
      });
    });
  }

  // ---- Kiválasztás + kosár-attribútumok ----
  async selectPoint(p) {
    this.state.selected = p;
    this.state.detailsMode = true;
    this._renderDetails();
    this._applyPanelMode();
    this._renderList();
    this._renderMap();
    this._clearError();
    try {
      await this._writeSelected(p);
    } catch (e) {
      this._showError('Nem sikerült menteni a választott pontot. Próbáld újra.');
    }
    this._applyCheckoutGate();
  }

  // ---- Kiválasztott pont részletei (kártya) ----
  _applyPanelMode() {
    const details = !!(this.state.detailsMode && this.state.selected);
    if (this.panel) this.panel.classList.toggle('is-details', details);
    if (this.detailsEl) this.detailsEl.hidden = !details;
    // a térkép szélessége változik (teljes <-> fél) -> Leaflet újraméretezés
    if (this.map) requestAnimationFrame(() => this.map && this.map.invalidateSize(false));
  }

  _chooseAnother() {
    // A korábban kiválasztott pontot törölni kell (állapotból ÉS a kosárból is) –
    // különben a listában továbbra is "Kiválasztva"-ként jelenne meg, és a Fizetés
    // gomb sem tiltódna le, pedig a vásárló még nem választott ÚJ pontot.
    this.state.selected = null;
    this.state.detailsMode = false;
    this._applyPanelMode();
    this._refreshPoints();
    this._writeMethodOnly();
    this._applyCheckoutGate();
  }

  _infoRow(icon, title, text) {
    return `<div class="kk-ship__drow"><span class="kk-ship__dicon">${DETAIL_ICONS[icon] || ''}</span>`
      + `<div class="kk-ship__drow-body"><strong>${title}</strong><span>${text}</span></div></div>`;
  }

  _openHoursHtml(open) {
    if (!open) return '';
    let val;
    if (typeof open === 'string') {
      const pretty = prettyHours(open);
      const full = pretty === '0–24' ? ' kk-ship__hours-pill--full' : '';
      val = `<span class="kk-ship__hours-pill${full}">${esc(pretty)}</span><span class="kk-ship__dmuted">minden nap</span>`;
    } else {
      const days = ['hetfo', 'kedd', 'szerda', 'csutortok', 'pentek', 'szombat', 'vasarnap'];
      val = '<div class="kk-ship__hours-week">' + days.filter((d) => open[d]).map((d) =>
        `<span class="kk-ship__hours-day"><span class="kk-ship__dmuted">${DAY_NAMES[d]}</span><span>${esc(prettyHours(open[d]))}</span></span>`
      ).join('') + '</div>';
    }
    return `<div class="kk-ship__hours"><strong>Nyitvatartás</strong><div class="kk-ship__hours-val">${val}</div></div>`;
  }

  _renderDetails() {
    if (!this.detailsEl) return;
    const p = this.state.selected;
    if (!p) { this.detailsEl.innerHTML = ''; return; }
    const dist = this.state.userLoc ? fmtDist(distKm(this.state.userLoc.lat, this.state.userLoc.lng, p.lat, p.lng)) : '';
    const price = this.config.prices && this.config.prices[p.carrier];
    const cmeta = CARRIER_META[p.carrier] || { name: '' };
    const priceHtml = price ? `<span class="kk-ship__dprice">${esc(cmeta.name)} · ${Number(price).toLocaleString('hu-HU')} Ft</span>` : '';
    const rows = [
      this._infoRow('clock', 'Várható átvétel', 'A csomag várhatóan 1–2 munkanapon belül átvehető.'),
      this._infoRow('mail', 'Értesítés', 'SMS-ben és e-mailben értesítünk, amikor a csomag megérkezik.'),
    ];
    if (p.directions) rows.push(this._infoRow('car', 'Megközelítés', esc(p.directions)));
    this.detailsEl.innerHTML = `
      <p class="kk-ship__step">Kiválasztott átvételi pont</p>
      <div class="kk-ship__dcard">
        <div class="kk-ship__dhead">
          ${this._listLogo(p)}
          <div class="kk-ship__dhead-body">
            <strong>${esc(p.name)}</strong>
            <span class="kk-ship__dhead-addr">${esc(addrOf(p))}</span>
            ${priceHtml}
          </div>
          ${dist ? `<span class="kk-ship__item-dist">${dist}</span>` : ''}
          <span class="kk-ship__dcheck">${CHECK_ICON}</span>
        </div>
        <div class="kk-ship__dinfo">
          ${this._openHoursHtml(p.open)}
          ${rows.join('')}
        </div>
        <button type="button" class="kk-ship__dchange" data-change>Másik átvételi pont választása</button>
        <div class="kk-ship__dpill">
          <span class="kk-ship__dpill-check">${CHECK_ICON}</span>
          <div><strong>A kiválasztott átvételi pont elérhető</strong><span>A csomagfeladás várhatóan 1–2 munkanapon belül történik.</span></div>
        </div>
      </div>`;
    const changeBtn = this.detailsEl.querySelector('[data-change]');
    if (changeBtn) changeBtn.addEventListener('click', () => this._chooseAnother());
  }

  _selectedAttrs(p) {
    const meta = CARRIER_META[p.carrier] || { name: '' };
    return {
      [ATTR.method]: this.config.labels.parcel,
      [ATTR.kind]: 'parcel',
      [ATTR.carrier]: meta.name,
      [ATTR.name]: p.name,
      [ATTR.address]: addrOf(p),
      [ATTR.city]: p.city,
      [ATTR.id]: p.operatorId || p.id,
      [ATTR.json]: JSON.stringify({ id: p.id, operatorId: p.operatorId, carrier: p.carrier, variant: p.variant, name: p.name, zip: p.zip, city: p.city, street: p.street, lat: p.lat, lng: p.lng }),
    };
  }

  _writeSelected(p) {
    return updateCartAttributes(this._selectedAttrs(p)).then((result) => {
      refreshCartSummary(this);
      return result;
    });
  }

  _writeMethodOnly() {
    // A pont-specifikus mezőket is töröljük – "Másik pont választása" után ezek a
    // korábbi (immár érvénytelen) pontra mutatnának, amíg a vásárló újat nem választ.
    return updateCartAttributes({
      [ATTR.method]: this.config.labels.parcel,
      [ATTR.kind]: 'parcel',
      [ATTR.carrier]: '',
      [ATTR.name]: '',
      [ATTR.address]: '',
      [ATTR.city]: '',
      [ATTR.id]: '',
      [ATTR.json]: '',
    }).then(() => refreshCartSummary(this)).catch(() => {});
  }

  _writeHome() {
    this.state.selected = null;
    return updateCartAttributes({
      [ATTR.method]: this.config.labels.home,
      [ATTR.kind]: 'home',
      [ATTR.carrier]: '',
      [ATTR.name]: '',
      [ATTR.address]: '',
      [ATTR.city]: '',
      [ATTR.id]: '',
      [ATTR.json]: '',
    }).then(() => refreshCartSummary(this)).catch(() => {});
  }

  // ---- Checkout gomb tiltása ----
  _applyCheckoutGate() {
    // mode: '' (nincs tiltás) | 'method' (nincs mód) | 'point' (automata, de nincs pont)
    let mode = '';
    if (!this.state.method) mode = 'method';
    else if (this.state.method === 'parcel' && !this.state.selected) mode = 'point';
    const gated = mode !== '';
    this._gate = gated;
    const btn = document.getElementById('checkout');
    if (btn) {
      if (gated) {
        btn.setAttribute('disabled', 'disabled');
        btn.dataset.kkGated = '1';
      } else if (btn.dataset.kkGated === '1') {
        btn.removeAttribute('disabled');
        delete btn.dataset.kkGated;
      }
    }
    // Pont-hiánynál a barátságos kártya, mód-hiánynál a rövid szöveg
    if (mode === 'point') {
      this._clearError();
      if (this.promptEl) this.promptEl.hidden = false;
    } else {
      if (this.promptEl) this.promptEl.hidden = true;
      if (mode === 'method') this._showError('Válassz szállítási módot a folytatáshoz.');
      else this._clearError();
    }
    this._watchGate(gated);
  }

  // A checkout gomb re-renderelődhet (morph). Csak amíg tiltunk, figyeljük, és
  // requestAnimationFrame-mel összevonjuk a mutációkat, hogy ne lassítsa az oldalt.
  _watchGate(on) {
    if (on) {
      if (!this._gateObserver) {
        const reapply = () => {
          this._gateScheduled = false;
          const btn = document.getElementById('checkout');
          // Csak akkor írunk, ha kell (a disabled attribútum-írás is mutációt kelt → végtelen kör lenne).
          if (btn && this._gate && (!btn.disabled || btn.dataset.kkGated !== '1')) { btn.setAttribute('disabled', 'disabled'); btn.dataset.kkGated = '1'; }
        };
        this._gateObserver = new MutationObserver(() => {
          if (!this._gateScheduled) { this._gateScheduled = true; requestAnimationFrame(reapply); }
        });
      }
      // A téma frissítése (morph) a gombot helyben módosítja (a disabled attribútumot törölheti), ezért az attribútumot is figyeljük.
      this._gateObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
    } else if (this._gateObserver) {
      this._gateObserver.disconnect();
    }
  }

  _showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  }
  _clearError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }
}

if (!customElements.get('shipping-selector')) {
  customElements.define('shipping-selector', ShippingSelector);
}
console.info('[KK shipping-selector] betöltve – verzió ' + VERSION);
